import {
  call,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects';

import { sendPostMessageToParent } from 'src/sdk/service/sdk.service';
import { SentryMethods } from 'src/utils/sentryMethods';
import {
  setReadingTopics,
  setReadingFeedbackVisibility,
  setReadingFeedbackChatContext,
  setReadingFeedbackData,
  setReadingFeedbackWebViewMode,
  setReadingFeedbackApiError,
  setReadingFeedbackLoading,
  resetReadingFeedback,
  handleRequestError,
  setLoadingState,
  showPopUpNotification,
  setPopUpNotificationData,
  openReadingFeedbackPopup,
} from 'actions/appActions';
import {
  FETCH_READING_TOPICS,
  SAVE_READING_FEEDBACK,
  SAVE_READING_FEEDBACK_DRAFT,
  OPEN_READING_FEEDBACK_POPUP,
} from 'actions/actionsTypes';
import { PopUpNotification } from 'constants/enums';
import { API } from 'src/utils/api';
import {
  selectExtId,
  selectReadingFeedbackChatContext,
  selectReadingFeedbackFormData,
  selectReadingFeedbackIsWebViewMode,
  selectUserAgent,
} from 'selectors/selectors';
import { descriptionFromError, getPlatform } from 'src/utils/commonUtil';
import { CLOSE_READING_FEEDBACK } from 'constants/constants';

const DEMO_READING_TOPICS = [
  'Love',
  'Career',
  'Life Path',
  'Money',
  'Pet',
  'Past Life',
  'Deceased Loved Ones',
  'Lost Objects',
  'Lost People & Pets',
  'Other',
];

const DEMO_FOLLOW_UP_OPTIONS = [
  { value: 'no_follow_up', label: 'No follow-up needed', apiId: -1 },
  { value: 'one_week', label: 'One Week', apiId: 7 },
  { value: 'two_weeks', label: 'Two Weeks', apiId: 14 },
  { value: 'one_month', label: 'One Month', apiId: 30 },
  { value: 'two_months', label: 'Two Months', apiId: 60 },
];

export { DEMO_FOLLOW_UP_OPTIONS };

let lastSavedPayloadJson: string | null = null;

export function resetLastSavedPayload() {
  lastSavedPayloadJson = null;
}

function hasAnyFormData(formData): boolean {
  return (
    formData?.rating !== null
    || !!formData?.memoryClue?.trim()
    || (formData?.selectedTopics?.length ?? 0) > 0
    || !!formData?.followUpTimeline?.trim()
    || !!formData?.peopleDiscussed?.trim()
    || !!formData?.readingComments?.trim()
  );
}

function buildFollowUpApiId(formData) {
  return DEMO_FOLLOW_UP_OPTIONS.find(
    (opt) => opt.value === formData.followUpTimeline,
  )?.apiId ?? 0;
}

function ratingToApiValue(rating: string | null): number | null {
  if (rating === 'thumbs_up') return 1;

  if (rating === 'thumbs_down') return 0;

  return null;
}

function buildPostPayload(chatContext, formData, userAgent, isDraft, extID) {
  const sourcePlatform = getPlatform(userAgent);

  return {
    ...(chatContext?.taskId
      ? { taskId: Number(chatContext.taskId) }
      : { chatId: Number(chatContext?.chatId) }),
    customerId: chatContext?.customerId,
    extId: chatContext?.psychicExtId || extID,
    readingNote: formData.readingComments,
    didConnectWithUser: ratingToApiValue(formData.rating),
    followUp: buildFollowUpApiId(formData),
    readingTopics: formData.selectedTopics.join(', '),
    memoryClue: formData.memoryClue,
    peopleDiscussed: formData.peopleDiscussed,
    sourcePlatform,
    isDraft,
    formInvokedTSUTC: chatContext?.formInvokedTSUTC,
  };
}

function buildPutPayload(chatContext, formData, extID) {
  return {
    readingNoteId: chatContext.commentId,
    modifiedBy: String(chatContext.psychicExtId || extID),
    readingNote: formData.readingComments,
    didConnectWithUser: ratingToApiValue(formData.rating),
    followUp: buildFollowUpApiId(formData),
    readingTopics: formData.selectedTopics.join(', '),
    memoryClue: formData.memoryClue,
    peopleDiscussed: formData.peopleDiscussed,
    formInvokedTSUTC: chatContext?.formInvokedTSUTC,
  };
}

function* fetchReadingTopicsSaga() {
  // TODO: replace with real API call when BE is ready
  yield put(setReadingTopics(DEMO_READING_TOPICS));
}

function* saveReadingFeedbackDraftSaga() {
  const chatContext = yield select(selectReadingFeedbackChatContext);
  const formData = yield select(selectReadingFeedbackFormData);
  const userAgent = yield select(selectUserAgent);
  const extID = yield select(selectExtId);

  if (!hasAnyFormData(formData)) return;

  const isUpdate = !!chatContext?.commentId;
  const payload = isUpdate
    ? buildPutPayload(chatContext, formData, extID)
    : buildPostPayload(chatContext, formData, userAgent, true, extID);
  const payloadJson = JSON.stringify(payload);

  if (payloadJson === lastSavedPayloadJson) return;

  try {
    if (isUpdate) {
      yield call(API.ReadingFeedback.update, payload);
    } else {
      yield call(API.ReadingFeedback.save, payload);
    }

    lastSavedPayloadJson = payloadJson;
  } catch (e) {
    yield call(SentryMethods.captureException, e);
  }
}

function* saveReadingFeedbackSaga() {
  const chatContext = yield select(selectReadingFeedbackChatContext);
  const formData = yield select(selectReadingFeedbackFormData);
  const isWebViewMode = yield select(selectReadingFeedbackIsWebViewMode);
  const extID = yield select(selectExtId);

  if (!hasAnyFormData(formData)) {
    yield put(setReadingFeedbackVisibility(false));
    yield put(resetReadingFeedback());

    if (!isWebViewMode) {
      yield put(showPopUpNotification(false));
    }

    return;
  }

  try {
    yield put(setLoadingState(true));
    const isUpdate = !!chatContext?.commentId;

    if (isUpdate) {
      const putPayload = buildPutPayload(chatContext, formData, extID);
      yield call(API.ReadingFeedback.update, putPayload);
      lastSavedPayloadJson = JSON.stringify(putPayload);
    } else {
      const userAgent = yield select(selectUserAgent);
      const postPayload = buildPostPayload(chatContext, formData, userAgent, false, extID);
      yield call(API.ReadingFeedback.save, postPayload);
      const draftPayload = buildPostPayload(chatContext, formData, userAgent, true, extID);
      lastSavedPayloadJson = JSON.stringify(draftPayload);
    }

    if (isWebViewMode) {
      yield sendPostMessageToParent({ command: CLOSE_READING_FEEDBACK });
    } else {
      yield put(setReadingFeedbackVisibility(false));
      yield put(resetReadingFeedback());
      yield put(showPopUpNotification(false));
    }
  } catch (e) {
    const requestErrorPayload = {
      isInvalidToken: false,
      errorText: e?.message,
      description: descriptionFromError(e),
    };
    yield call(SentryMethods.captureException, e);
    yield put(handleRequestError(requestErrorPayload));
    console.error(e);

    if (isWebViewMode) {
      yield put(setReadingFeedbackApiError(true));
    }
  } finally {
    yield put(setLoadingState(false));
  }
}

function* handleOpenReadingFeedbackPopup({
  payload,
}: ReturnType<typeof openReadingFeedbackPopup>) {
  const {
    chatId,
    taskId,
    customerId,
    psychicExtId,
    customerName,
    firstName,
    readingTime,
    isWebViewMode = false,
  } = payload;

  try {
    yield put(setReadingFeedbackLoading(true));
    yield put(setLoadingState(true));

    let existingFeedback: any = null;

    if (chatId || taskId) {
      const existingFeedbackResponse = yield call(API.ReadingFeedback.get, {
        customerId,
        extId: psychicExtId,
        ...(taskId ? { taskId } : { chatId }),
      });

      existingFeedback = existingFeedbackResponse?.readingFeedback;
    }

    yield put(setReadingFeedbackChatContext({
      chatId: chatId || '',
      taskId,
      customerName,
      firstName: firstName || customerName,
      readingTime,
      existingReadingComments: existingFeedback?.body || '',
      customerId,
      psychicExtId,
      formInvokedTSUTC: new Date().toISOString(),
      commentId: existingFeedback?.commentId || undefined,
    }));

    if (existingFeedback) {
      const followUpTimeline = existingFeedback.followUp === 0 ? ''
        : DEMO_FOLLOW_UP_OPTIONS.find((opt) => opt.apiId === existingFeedback.followUp)?.value || '';

      const selectedTopics = existingFeedback.readingTopics
        ? existingFeedback.readingTopics.split(',').map((t) => t.trim()).filter(Boolean)
        : [];

      let rating: 'thumbs_up' | 'thumbs_down' | null = null;

      if (existingFeedback.didConnectWithUser != null) {
        rating = existingFeedback.didConnectWithUser ? 'thumbs_up' : 'thumbs_down';
      }

      yield put(setReadingFeedbackData({
        rating,
        followUpTimeline,
        selectedTopics,
        memoryClue: existingFeedback.memoryClue || '',
        peopleDiscussed: existingFeedback.peopleDiscussed || '',
        readingComments: existingFeedback.body || '',
      }));
    }

    if (isWebViewMode) {
      yield put(setReadingFeedbackWebViewMode(true));
    }

    yield put(setReadingFeedbackVisibility(true));

    if (!isWebViewMode) {
      yield put(setPopUpNotificationData({
        title: 'Reading Feedback',
        notificationType: PopUpNotification.READING_FEEDBACK,
      }));
      yield put(showPopUpNotification(true));
    }
  } catch (e) {
    yield call(SentryMethods.captureException, e);
  } finally {
    yield put(setReadingFeedbackLoading(false));
    yield put(setLoadingState(false));
  }
}

export function* readingFeedbackSagaWatcher() {
  yield takeLatest(FETCH_READING_TOPICS, fetchReadingTopicsSaga);
  yield takeLatest(SAVE_READING_FEEDBACK, saveReadingFeedbackSaga);
  yield takeLatest(SAVE_READING_FEEDBACK_DRAFT, saveReadingFeedbackDraftSaga);
  yield takeLatest(OPEN_READING_FEEDBACK_POPUP, handleOpenReadingFeedbackPopup);
}
